import React, { useState } from "react";
import { INVITE_ERRORS, resolveInviteCode } from "./inviteCode";

// The invite-code field, and the only place the code is validated for a typed
// entry. Shared by the League Play modal and the invite gate on a private league,
// so both accept the same input, show the same errors, and land in the same place.
//
// Errors are ALWAYS inline, under the field. A wrong code must never move the
// user off the page they're standing on.
const InviteCodeForm = ({ onResolved, autoFocus = true, submitLabel = "Join League" }) => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (checking) return;
    if (!code.trim()) {
      setError(INVITE_ERRORS.notFound);
      return;
    }
    setError("");
    setChecking(true);
    try {
      const result = await resolveInviteCode(code);
      if (!result.ok) {
        setError(result.message);
        return;
      }
      onResolved(result);
    } catch (err) {
      console.error("Invite code verification failed:", err);
      setError(INVITE_ERRORS.offline);
    } finally {
      setChecking(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <label
        htmlFor="invite-code-input"
        className="block text-[11px] font-semibold uppercase tracking-wider text-[#0F4146]"
      >
        Invite code
      </label>
      <input
        id="invite-code-input"
        type="text"
        value={code}
        onChange={(e) => {
          // Codes are upper-case and unspaced; strip whitespace as it arrives so a
          // pasted code carrying a stray space or newline still works.
          setCode(e.target.value.replace(/\s+/g, "").toUpperCase());
          if (error) setError("");
        }}
        placeholder="e.g. CARTER25"
        className={`mt-1.5 w-full rounded-xl border bg-white px-3.5 py-3.5 text-base tracking-wide text-[#0F172A] placeholder:text-tertiary placeholder:tracking-normal focus:outline-none focus:ring-2 focus:ring-[#004C50]/20 ${
          error ? "border-red-400" : "border-[#004C50]"
        }`}
        autoFocus={autoFocus}
        autoCapitalize="characters"
        autoCorrect="off"
        spellCheck={false}
        autoComplete="off"
        aria-invalid={!!error}
        aria-describedby={error ? "invite-code-error" : undefined}
      />

      {error && (
        <p id="invite-code-error" className="mt-2 text-sm text-red-500">
          {error}
        </p>
      )}

      <button
        type="submit"
        disabled={checking}
        className="mt-4 w-full rounded-xl bg-[#004C50] text-white py-3.5 text-base font-semibold hover:bg-[#0B3A3F] transition-colors disabled:opacity-60"
      >
        {checking ? "Checking…" : submitLabel}
      </button>
    </form>
  );
};

export default InviteCodeForm;
